import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Guards a navigation that follows an `await`, such as opening a page once a
 * save finishes. Call `const stillHere = begin()` before the await and
 * navigate only if `stillHere()`: it is true while the component is mounted
 * and the router is still on the location it was on when `begin()` ran. The
 * user's own navigation meanwhile (Back into another org, another trial in
 * the same org) wins over the stale destination. `begin` is stable.
 */
export function useNavigationGuard(): () => () => boolean {
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  // Once unmounted, locationRef stops updating, so it can no longer tell where the user went.
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return useCallback(() => {
    const from = locationRef.current;
    return () => mountedRef.current && locationRef.current === from;
  }, []);
}
